import { listPaddocksByOrganizationQueryOptions } from "@packages/api-client/hooks/paddockController";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { SoilLabReportImportWizard } from "@/components/grass/soil/import/SoilLabReportImportWizard";
import { DocumentImportWorkspace } from "@/components/import/DocumentImportWorkspace";
import { Eyebrow } from "@/components/import/ImportPrimitives";
import { requireReachableRoute } from "@/lib/offline/routeGuard";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/grass-production/farms/$farmId/soil-reports/import",
)({
	// Lab-report import is desk work (file upload + AI parsing) — online-only.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: SoilReportImportPage,
});

function SoilReportImportPage() {
	const { t } = useTranslation();
	const { orgId, farmId } = Route.useParams();
	const navigate = useNavigate();

	const { data: paddocksData } = useQuery(
		listPaddocksByOrganizationQueryOptions(orgId, { establishmentId: farmId }),
	);
	const paddocks = (paddocksData?.data ?? []).map((p) => ({
		id: p.id,
		name: p.name,
	}));

	// Both leaving the wizard (imported) and backing out of it (declined the
	// step-1 acknowledgement) land on the same place the producer came from.
	const backToHub = () =>
		navigate({
			to: "/app/org/$orgId/grass-production",
			params: { orgId },
			// Land on the hub with this farm preselected (the per-farm dashboard
			// was consolidated into the hub).
			search: { farm: farmId },
		});

	return (
		<div className="mx-auto flex w-full max-w-[1080px] flex-col gap-4 md:gap-6">
			<div className="flex flex-col gap-1 md:gap-1.5">
				<Eyebrow>{t("grass.soilLabReport.eyebrow")}</Eyebrow>
				<h1
					className="font-display text-[clamp(24px,4vw,40px)] font-extrabold leading-[1.1] tracking-[-0.02em] text-base-content"
					data-testid="page-title"
				>
					{t("grass.soilLabReport.pageTitle")}
				</h1>
				<p className="max-w-[56ch] text-sm leading-relaxed text-base-content/70 md:text-[15px]">
					{t("grass.soilLabReport.pageSubtitle")}
				</p>
			</div>
			<DocumentImportWorkspace
				orgId={orgId}
				kind="soil-reports"
				establishmentId={farmId}
			>
				{({ resume, sessionKey, onUploaded, onDone }) => (
					<SoilLabReportImportWizard
						key={`${resume?.id ?? "new"}-${sessionKey}`}
						orgId={orgId}
						farmId={farmId}
						paddocks={paddocks}
						resume={
							resume
								? {
										mediaFileId: resume.id,
										status: resume.status as
											| "PROCESSING"
											| "READY_FOR_REVIEW"
											| "FAILED",
										analysisId: resume.analysisId,
										extraction: resume.result,
									}
								: undefined
						}
						onUploaded={onUploaded}
						onDone={() => {
							onDone();
							void backToHub();
						}}
						onCancel={backToHub}
					/>
				)}
			</DocumentImportWorkspace>
		</div>
	);
}
